import React from "react"
import QuotaLimitExceededError from "src/validation/exception_messages/QuotaLimitExceededError";
import ServerError from "src/validation/exception_messages/ServerError";
import ThrottleLimitExceededError from "src/validation/exception_messages/ThrottleLimitExceededError";
import UserInputError from "src/validation/exception_messages/UserInputError";
import Helmet from "react-helmet";

export interface ErrorRenderOptions {
    onDismiss?: () => void
}

class ErrorHandler {
    render(error: any, options?: ErrorRenderOptions) {
        if (!error) {
            return null
        }
        const status = this.getHttpStatus(error)
        const reason = this.getReason(error)

        let message = "Oh no! There's been an error."
        if (error instanceof UserInputError) {
            message = "Hold on a minute! There was an error with the inputs you supplied"
        }
        if (error instanceof ThrottleLimitExceededError) {
            message = "Wow! Slow down there partner you've made a lot of requests in a short span of time. Wait the allotted time and try again."
        }
        if (error instanceof QuotaLimitExceededError) {
            message = "Damn! You've reached your limit for today, come back again tomorrow or consider going premium if you want to make more requests."
        }
        if (error instanceof ServerError) {
            message = "Yikes! We seem to be having some issues with our servers. Try again in a few minutes."
        }
        // For non-standard error shapes that still include an HTTP status, surface the reason for 4XX.
        if (status && 400 <= status && status < 500) {
            message = reason || message
        }
        const shouldNoIndex = this.shouldNoIndex(error)
        const onDismiss = options?.onDismiss

        return (
            <div
                className="error-overlay"
                id="user-visible-error-field"
                data-error-type={typeof error}
                onClick={onDismiss || undefined}
            >
                {shouldNoIndex && <Helmet><meta name="robots" content="noindex"/></Helmet>}
                <div className="error-container" onClick={(e) => e.stopPropagation()}>
                    {onDismiss && (
                        <button
                            type="button"
                            className="error-close"
                            onClick={(e) => { e.stopPropagation(); onDismiss() }}
                            onMouseDown={(e) => e.stopPropagation()}
                            aria-label="Dismiss"
                        >
                            ×
                        </button>
                    )}
                    <div className="error-warning" id="user-visible-error-warning-field">{message}</div>
                    <div className="error-message" id="user-visible-error-message-field">[ {reason} ]</div>
                </div>
            </div>
        )
    }

    getHttpStatus(error: any): number | undefined {
        const status = error?.status ?? error?.statusCode ?? error?.response?.status
        return typeof status === "number" ? status : undefined
    }

    getReason(error: any): string {
        const candidate =
            error?.message ??
            error?.error ??
            error?.response?.data?.error ??
            error?.response?.data?.message ??
            error?.response?.data ??
            error?.data ??
            error

        if (typeof candidate === "string") return candidate
        try {
            return JSON.stringify(candidate)
        } catch {
            return String(candidate)
        }
    }

    shouldNoIndex(error: any) {
        if (!error.message) {
            return false
        }
        // Tell googlebot to not index the page if the error is because no nearby sold homes were found
        if (error.message.includes("No nearby sold homes were found")) {
            return true
        }
        return false
    }

    /** True when valuation cannot be computed (no nearby sold homes). */
    static isNoNearbyHomesError(error: any): boolean {
        const msg = error?.message ?? ""
        return typeof msg === "string" && msg.includes("No nearby sold homes were found")
    }

    /** True when area was not found or cannot be loaded. */
    static isAreaNotFoundError(error: any): boolean {
        const status = error?.status ?? error?.statusCode ?? error?.response?.status
        if (status === 404) return true
        const msg = (error?.message ?? error?.error ?? "").toString().toLowerCase()
        return msg.includes("not found") || msg.includes("no area") || msg.includes("no such area")
    }
}

export default ErrorHandler
